import { createSelector } from '@reduxjs/toolkit';

import { Features } from '@/shared/types/account';
import { Tool } from '@/shared/types/brain';
import { RootState } from '@/shared/types/state';

/**
 * Selector function that retrieves the feature flags from the Redux store state.
 *
 * @param state - The root state of the Redux store
 * @returns The feature flags object from the store state
 */
export const selectFeatureFlags = (state: RootState): Features => state.featureFlags;

/**
 * Whether the in-webchat voice call feature should be surfaced.
 *
 * Gated behind `web_voice` — the same flag that makes the web-voice
 * integration available on the connections page, so an account that can
 * create a web-voice integration can always pair it with the webchat.
 *
 * Default-OFF: an absent/undefined flag keeps the feature hidden.
 */
export const selectIsWebchatVoiceEnabled = (state: RootState): boolean =>
  Boolean(state.featureFlags.web_voice);

/**
 * Whether the webchat teaser is offered at all on the integration's settings.
 *
 * Default-OFF: an absent/undefined flag hides the whole block, and Start
 * behavior looks exactly as it does without this feature — the welcome message
 * and nothing else. The teaser needs an orchestrator endpoint and a channels
 * change that ship separately, so the flag is what keeps this from having to be
 * reverted if either slips.
 *
 * The flag key still reads `webchat_teaser_generation`, which is narrower than
 * what it now controls. It is deliberately not renamed — the key is configured
 * per account outside this repo, and a rename there is not worth the churn.
 */
export const selectIsWebchatTeaserEnabled = (state: RootState): boolean =>
  Boolean(state.featureFlags.webchat_teaser_generation);

/**
 * Whether Google Drive is offered as a knowledge base source.
 *
 * Default-OFF: an absent/undefined flag hides the option, so the source list
 * reads exactly as it did before Google Drive existed. The sign-in, the folder
 * picker and the scheduled sync all depend on a Google project we still have to
 * exercise end to end, so the flag keeps the option on the accounts we test
 * with until that is done.
 *
 * The deployment also has to carry the `GOOGLE_DRIVE_*` configuration, which
 * `useGoogleDriveSettings` reports. Both have to say yes.
 */
export const selectIsGoogleDriveEnabled = (state: RootState): boolean =>
  Boolean(state.featureFlags.google_drive);

/**
 * The local tools an AI agent can be offered, taking the feature flags into account.
 *
 * `ask_questions` stays hidden until `question_set` is on, the flag that gates the Question Set
 * node action. A brain that already has it in `tools` keeps it; only the toggle is hidden.
 */
export const selectAvailableLocalTools = createSelector(
  (state: RootState) => state.featureFlags,
  (featureFlags): Tool[] =>
    Object.values(Tool).filter((tool) => tool !== Tool.ASK_QUESTIONS || featureFlags.question_set)
);
